import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Settings,
  TestTube,
  Trash2,
  Info,
} from "lucide-react";
import axios from "axios";
import { config } from "../config";

interface OAuthStatus {
  status: string;
  message: string;
  details: {
    oauth_configured: boolean;
    tokens_available: boolean;
    org_configured: boolean;
    token_valid: boolean;
    client_id_configured: boolean;
    client_secret_configured: boolean;
    redirect_uri_configured: boolean;
  };
  configuration: {
    redirect_uri: string;
    scope: string;
  };
}

interface TestResults {
  status: string;
  message: string;
  test_results: {
    [key: string]: {
      status: string;
      count?: number;
      error?: string;
    };
  };
  timestamp: string;
}

const ZohoOAuthManager: React.FC = () => {
  const { user } = useAuth();
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus | null>(null);
  const [testResults, setTestResults] = useState<TestResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchOAuthStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${config.API_URL}/auth/zoho/status`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      setOauthStatus(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to fetch OAuth status");
    } finally {
      setIsLoading(false);
    }
  };

  const initiateOAuthFlow = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(
        `${config.API_URL}/auth/zoho/authorize`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );

      const { authorization_url } = response.data;

      // Open OAuth flow in new window
      const oauthWindow = window.open(
        authorization_url,
        "zoho_oauth",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );

      // Monitor the OAuth window
      const checkClosed = setInterval(() => {
        if (oauthWindow?.closed) {
          clearInterval(checkClosed);
          // Refresh status after OAuth completion
          setTimeout(() => {
            fetchOAuthStatus();
          }, 1000);
        }
      }, 1000);

      setSuccess(
        "OAuth authorization window opened. Complete the flow to authenticate."
      );
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to initiate OAuth flow");
    } finally {
      setIsLoading(false);
    }
  };

  const testConnection = async () => {
    try {
      setIsTesting(true);
      setError(null);
      const response = await axios.post(
        `${config.API_URL}/auth/zoho/test-connection`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      );
      setTestResults(response.data);
      setSuccess("Connection test completed successfully");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Connection test failed");
    } finally {
      setIsTesting(false);
    }
  };

  const revokeTokens = async () => {
    if (
      !window.confirm(
        "Are you sure you want to revoke OAuth tokens? This will require re-authentication."
      )
    ) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await axios.delete(`${config.API_URL}/auth/zoho/revoke`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      setSuccess("OAuth tokens revoked successfully");
      fetchOAuthStatus();
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to revoke tokens");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOAuthStatus();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "authenticated":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "tokens_expired":
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case "not_authenticated":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "not_configured":
        return <Settings className="w-5 h-5 text-gray-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "authenticated":
        return "bg-green-50 border-green-200 text-green-800";
      case "tokens_expired":
        return "bg-yellow-50 border-yellow-200 text-yellow-800";
      case "not_authenticated":
        return "bg-red-50 border-red-200 text-red-800";
      case "not_configured":
        return "bg-gray-50 border-gray-200 text-gray-800";
      default:
        return "bg-blue-50 border-blue-200 text-blue-800";
    }
  };

  if (!user?.is_superuser) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-center h-32">
          <XCircle className="w-8 h-8 text-red-500" />
          <span className="ml-2 text-lg font-medium text-gray-700 dark:text-gray-300">
            Admin access required
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Shield className="w-8 h-8 text-blue-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Zoho Desk OAuth
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Manage Zoho Desk API authentication
              </p>
            </div>
          </div>
          <button
            onClick={fetchOAuthStatus}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Status Card */}
      {oauthStatus && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Authentication Status
            </h3>
            <div className="flex items-center space-x-2">
              {getStatusIcon(oauthStatus.status)}
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(
                  oauthStatus.status
                )}`}
              >
                {oauthStatus.status.replace("_", " ").toUpperCase()}
              </span>
            </div>
          </div>

          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {oauthStatus.message}
          </p>

          {/* Configuration Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="flex items-center space-x-2">
              {oauthStatus.details.client_id_configured ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Client ID
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {oauthStatus.details.client_secret_configured ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Client Secret
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {oauthStatus.details.redirect_uri_configured ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Redirect URI
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {oauthStatus.details.tokens_available ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500" />
              )}
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Tokens
              </span>
            </div>
          </div>

          {/* Configuration Info */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              Configuration
            </h4>
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Redirect URI:
                </span>
                <span className="ml-2 text-gray-600 dark:text-gray-400 font-mono">
                  {oauthStatus.configuration.redirect_uri}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Scope:
                </span>
                <span className="ml-2 text-gray-600 dark:text-gray-400">
                  {oauthStatus.configuration.scope}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Actions
        </h3>

        <div className="flex flex-wrap gap-4">
          {oauthStatus?.status === "not_authenticated" && (
            <button
              onClick={initiateOAuthFlow}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Start OAuth Flow</span>
            </button>
          )}

          {oauthStatus?.status === "authenticated" && (
            <button
              onClick={testConnection}
              disabled={isTesting}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <TestTube
                className={`w-4 h-4 ${isTesting ? "animate-spin" : ""}`}
              />
              <span>Test Connection</span>
            </button>
          )}

          {oauthStatus?.status === "tokens_expired" && (
            <button
              onClick={initiateOAuthFlow}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh Tokens</span>
            </button>
          )}

          {oauthStatus?.details.tokens_available && (
            <button
              onClick={revokeTokens}
              disabled={isLoading}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              <span>Revoke Tokens</span>
            </button>
          )}
        </div>
      </div>

      {/* Test Results */}
      {testResults && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Connection Test Results
          </h3>

          <div className="mb-4">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium border ${
                testResults.status === "success"
                  ? "bg-green-50 border-green-200 text-green-800"
                  : testResults.status === "partial"
                  ? "bg-yellow-50 border-yellow-200 text-yellow-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
            >
              {testResults.status.toUpperCase()}
            </span>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {testResults.message}
            </p>
          </div>

          <div className="space-y-3">
            {Object.entries(testResults.test_results).map(
              ([endpoint, result]) => (
                <div
                  key={endpoint}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    {result.status === "success" ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <span className="font-medium text-gray-900 dark:text-white capitalize">
                      {endpoint.replace("_", " ")}
                    </span>
                  </div>
                  <div className="text-right">
                    {result.status === "success" ? (
                      <span className="text-green-600 dark:text-green-400">
                        {result.count} items
                      </span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400 text-sm">
                        {result.error}
                      </span>
                    )}
                  </div>
                </div>
              )
            )}
          </div>

          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Test completed at:{" "}
            {new Date(testResults.timestamp).toLocaleString()}
          </div>
        </div>
      )}

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-800 font-medium">Error</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-800 font-medium">Success</span>
          </div>
          <p className="text-green-700 mt-1">{success}</p>
        </div>
      )}

      {/* Setup Instructions */}
      {oauthStatus?.status === "not_configured" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">
            Setup Instructions
          </h3>
          <div className="space-y-4 text-blue-800">
            <div>
              <h4 className="font-medium mb-2">
                1. Create Zoho Desk API Application
              </h4>
              <p className="text-sm">
                Go to the Zoho Developer Console and create a new application
                for Desk API access.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">
                2. Configure Environment Variables
              </h4>
              <p className="text-sm">
                Add the following variables to your backend .env file:
              </p>
              <div className="mt-2 bg-blue-100 p-3 rounded font-mono text-sm">
                <div>ZOHO_DESK_CLIENT_ID=your_client_id</div>
                <div>ZOHO_DESK_CLIENT_SECRET=your_client_secret</div>
                <div>
                  ZOHO_DESK_REDIRECT_URI=http://localhost:8000/auth/zoho/callback
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-2">3. Restart Application</h4>
              <p className="text-sm">
                Restart your backend application to load the new configuration.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ZohoOAuthManager;
